import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row,
} from "reactstrap";
import { errorToast, successToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";
import { formatCurrency } from "../../utils/formatCurrency";
import { creditWalletCustomer, debitWalletCustomer } from "../../utils/apiCalls";

const CREDIT_REASONS = [
  "Service recovery — order issue",
  "Goodwill gesture",
  "Compensation — late order",
  "Marketing campaign credit",
  "Manual correction",
];
const DEBIT_REASONS = [
  "Reversing a credit applied in error",
  "Manual correction",
  "Fraud investigation",
];

const newRequestId = () => {
  // crypto.randomUUID is available in every browser this panel supports; the fallback exists only so
  // a non-secure-context dev environment cannot silently produce a colliding id.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `adj-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const AdjustWalletModal = ({
  isOpen,
  toggle,
  merchantId,
  merchantName,
  customer,               // { id, firstName, lastName, phoneNo }
  purchasedBalance = 0,
  promotionalBalance = 0,
  isPlatformAdmin = false, // only a platform admin may credit the transferable bucket
  promotionalOnly = false, // operations staff: promotional in both directions, never purchased
  onDone,                 // called after a successful adjustment so the parent can refresh
}) => {
  const [direction, setDirection] = useState("credit");
  const [bucket, setBucket] = useState("promotional");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);

  // A fresh requestId per OPEN. Regenerating per click would defeat idempotency entirely; reusing one
  // across opens would make a genuine second adjustment silently vanish.
  useEffect(() => {
    if (isOpen) {
      setRequestId(newRequestId());
      setDirection("credit");
      setBucket("promotional");
      setAmount("");
      setReason("");
      setSubmitting(false);
      setSubmitted(false);
    }
  }, [isOpen]);

  const value = Number(amount) || 0;
  const isCredit = direction === "credit";
  const purchasedLocked = promotionalOnly || (isCredit && !isPlatformAdmin);

  const currentBucket = bucket === "purchased" ? Number(purchasedBalance) : Number(promotionalBalance);
  const total = Number(purchasedBalance) + Number(promotionalBalance);
  const delta = isCredit ? value : -value;
  const nextBucket = Math.round((currentBucket + delta) * 100) / 100;
  const nextTotal = Math.round((total + delta) * 100) / 100;

  const errors = useMemo(() => {
    const e = [];
    if (value <= 0) e.push("Enter an amount greater than zero.");
    if (reason.trim().length < 3) e.push("A reason is required (at least 3 characters).");
    // Mirrors the server: only a platform admin may mint transferable credit, because credit that can
    // be sent onward is a laundering path in a way spend-only credit is not.
    if (bucket === "purchased" && promotionalOnly) {
      e.push("Operations staff can only adjust promotional credit.");
    } else if (isCredit && bucket === "purchased" && !isPlatformAdmin) {
      e.push("Only a platform admin can add purchased (transferable) credit. Use promotional instead.");
    }
    // The server refuses to overdraw too; catching it here means the operator is told before the
    // money-moving request rather than after.
    if (!isCredit && value > currentBucket) {
      e.push(`Not enough ${bucket} credit — the customer has ${formatCurrency(currentBucket)}.`);
    }
    return e;
  }, [value, reason, isCredit, bucket, isPlatformAdmin, promotionalOnly, currentBucket]);

  const submit = async () => {
    if (errors.length || submitting || submitted) return;
    setSubmitting(true);

    const payload = {
      merchantId,
      // Always POSITIVE. The endpoint decides the sign, so there is no way for a mistyped minus to
      // invert the operation.
      amount: value,
      bucket,
      reason: reason.trim(),
      requestId,
    };

    const result = isCredit
      ? await creditWalletCustomer(customer.id, payload)
      : await debitWalletCustomer(customer.id, payload);

    setSubmitting(false);

    if (!result.ok) {
      // Left unlocked so the operator can correct and retry — the SAME requestId is reused, so a
      // retry after a network error that actually succeeded server-side is still a no-op.
      return errorToast(result.message || "The adjustment was not applied");
    }

    setSubmitted(true);
    if (result.data?.alreadyApplied) {
      successToast("Already applied — this adjustment had gone through already");
    } else {
      successToast(isCredit ? "Balance added" : "Balance deducted");
    }
    if (onDone) onDone();
  };

  const customerName = [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") || "—";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Adjust wallet balance</ModalHeader>
      <ModalBody>
        {/* Read-only context. An operator who has several tabs open needs to see WHO and WHICH BRAND
            before moving money, not infer it from the page behind the modal. */}
        <div style={{ background: C.bg, borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <Row style={{ fontSize: 13 }}>
            <Col xs="6">
              <div style={{ color: C.textLight, fontSize: 11 }}>CUSTOMER</div>
              <div style={{ color: C.text, fontWeight: 600 }}>{customerName}</div>
              <div style={{ color: C.textMed }}>{customer?.phoneNo || "—"}</div>
            </Col>
            <Col xs="6">
              <div style={{ color: C.textLight, fontSize: 11 }}>BRAND</div>
              <div style={{ color: C.text, fontWeight: 600 }}>{merchantName || `Merchant ${merchantId}`}</div>
            </Col>
          </Row>
          <hr style={{ margin: "10px 0", borderColor: C.border }} />
          <Row style={{ fontSize: 13 }}>
            <Col xs="4">
              <div style={{ color: C.textLight, fontSize: 11 }}>PURCHASED</div>
              <div style={{ color: C.text }}>{formatCurrency(purchasedBalance)}</div>
            </Col>
            <Col xs="4">
              <div style={{ color: C.textLight, fontSize: 11 }}>PROMOTIONAL</div>
              <div style={{ color: C.text }}>{formatCurrency(promotionalBalance)}</div>
            </Col>
            <Col xs="4">
              <div style={{ color: C.textLight, fontSize: 11 }}>TOTAL</div>
              <div style={{ color: C.text, fontWeight: 700 }}>{formatCurrency(total)}</div>
            </Col>
          </Row>
        </div>

        {/* Segmented direction control. Two explicit buttons rather than a signed number field. */}
        <FormGroup>
          <Label style={{ fontSize: 13, color: C.textMed }}>Direction</Label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { key: "credit", label: "Add balance", color: C.green, bg: C.greenLight },
              { key: "debit", label: "Deduct balance", color: C.orange, bg: C.orangeLight },
            ].map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setDirection(d.key)}
                disabled={submitted}
                style={{
                  flex: 1, padding: "10px 12px", borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: submitted ? "not-allowed" : "pointer",
                  border: `2px solid ${direction === d.key ? d.color : C.border}`,
                  background: direction === d.key ? d.bg : C.white,
                  color: direction === d.key ? d.color : C.textMed,
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        </FormGroup>

        <FormGroup>
          <Label style={{ fontSize: 13, color: C.textMed }}>Bucket</Label>
          <Input type="select" value={bucket} onChange={(e) => setBucket(e.target.value)} disabled={submitted}>
            <option value="promotional">Promotional — spendable on orders, not transferable</option>
            <option value="purchased" disabled={purchasedLocked}>
              Purchased — transferable and refundable
              {promotionalOnly ? " (not for operations)" : purchasedLocked ? " (platform admin only)" : ""}
            </option>
          </Input>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
            Compensation normally goes to <strong>promotional</strong>. Only money the customer
            actually paid belongs in purchased.
          </div>
        </FormGroup>

        <FormGroup>
          <Label style={{ fontSize: 13, color: C.textMed }}>Amount</Label>
          <Input
            type="number" min="0" step="0.01" value={amount} disabled={submitted}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
        </FormGroup>

        <FormGroup>
          <Label style={{ fontSize: 13, color: C.textMed }}>Reason (recorded on the ledger)</Label>
          <Input
            type="select" value="" disabled={submitted}
            onChange={(e) => e.target.value && setReason(e.target.value)}
            style={{ marginBottom: 8 }}
          >
            <option value="">Choose a common reason…</option>
            {(isCredit ? CREDIT_REASONS : DEBIT_REASONS).map((r) => <option key={r} value={r}>{r}</option>)}
          </Input>
          <Input
            type="textarea" rows={2} value={reason} disabled={submitted}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this adjustment being made?"
          />
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
            Stored permanently against this adjustment and visible to anyone auditing the wallet.
          </div>
        </FormGroup>

        {/* Live resulting balance. The single most useful check against a mistyped amount. */}
        {value > 0 && (
          <div style={{ background: isCredit ? C.greenLight : C.orangeLight, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: C.textMed }}>RESULT</div>
            <div style={{ fontSize: 14, color: C.text, marginTop: 4 }}>
              {bucket} {formatCurrency(currentBucket)} →{" "}
              <strong>{formatCurrency(nextBucket)}</strong>
            </div>
            <div style={{ fontSize: 13, color: C.textMed, marginTop: 2 }}>
              total {formatCurrency(total)} → <strong>{formatCurrency(nextTotal)}</strong>
            </div>
          </div>
        )}

        {errors.length > 0 && !submitted && (
          <div style={{ marginTop: 12, padding: 10, background: "#FEF2F2", borderRadius: 6 }}>
            {errors.map((e) => <div key={e} style={{ fontSize: 12, color: "#B91C1C" }}>• {e}</div>)}
          </div>
        )}

        {submitted && (
          <div style={{ marginTop: 12, padding: 10, background: C.greenLight, borderRadius: 6, fontSize: 13, color: C.text }}>
            Adjustment applied. Close this window to make another.
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>{submitted ? "Close" : "Cancel"}</Button>
        <Button
          color={isCredit ? "success" : "warning"}
          onClick={submit}
          // Locked on `submitted` as well as `submitting`: the only route to a second adjustment is a
          // reopen, which mints a new requestId.
          disabled={submitting || submitted || errors.length > 0}
        >
          {submitting ? "Applying…" : isCredit ? `Add ${formatCurrency(value)}` : `Deduct ${formatCurrency(value)}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AdjustWalletModal;
